/* global console, fetch */

import type { AgreementsActivityResult } from '../../activity-handler/agreements-activity.types';
import { ACTIVITY_HANDLER_URL } from '../../activity-handler/activity-handler.constants';
import { buildActivityRequestHeaders } from '../../activity-handler/activity-request-headers.util';
import type { DwtPayloadsPayload } from '../models/dwt-payloads.model';
import { validateDwtPayloadsFilterRequest } from '../utils/validate-dwt-payloads-filter.util';

/**
 * POST Get-RegulatoryReports to the Activity Handler (feature-owned transport + response shaping).
 */
export async function fetchGetDwtPayloadsActivity(
    accessToken: string,
    payload: DwtPayloadsPayload,
    activityHandlerUrl: string = ACTIVITY_HANDLER_URL
): Promise<AgreementsActivityResult> {
    const validation = validateDwtPayloadsFilterRequest(payload);
    if (validation.ok === false) {
        return {
            kind: 'failure',
            message: validation.message,
            source: 'validation',
            invalidElementIds: validation.invalidElementIds,
        };
    }

    try {
        const response = await fetch(activityHandlerUrl, {
            method: 'POST',
            headers: buildActivityRequestHeaders(accessToken),
            body: JSON.stringify(payload),
        });

        const body: unknown = await response.json().catch(() => null);
        const data =
            body !== null && typeof body === 'object' && 'data' in body
                ? (body as { data?: unknown }).data
                : undefined;
        const records =
            data !== null && typeof data === 'object' && data !== null && 'records' in data
                ? (data as { records?: unknown }).records
                : undefined;
        const hasRecordsArray = Array.isArray(records);
        const totalCount =
            data !== null && typeof data === 'object' && data !== null && 'totalCount' in data
                ? (data as { totalCount?: unknown }).totalCount
                : undefined;
        const recordCount = hasRecordsArray ? records.length : 0;

        if (!response.ok && !hasRecordsArray) {
            const errorMessage =
                body !== null &&
                typeof body === 'object' &&
                'message' in body &&
                typeof (body as { message: unknown }).message === 'string'
                    ? (body as { message: string }).message
                    : `Request failed with status ${response.status}`;
            return {
                kind: 'failure',
                message: errorMessage,
                source: 'http',
                httpStatus: response.status,
                body,
            };
        }

        if (!response.ok && hasRecordsArray) {
            console.warn(
                `Non-OK status ${response.status} received, but payload contains records. Continuing.`
            );
        }

        return {
            kind: 'success',
            httpStatus: response.status,
            body,
            records: hasRecordsArray ? records : [],
            totalCount: typeof totalCount === 'number' ? totalCount : undefined,
            recordCount,
        };
    } catch (error) {
        return {
            kind: 'failure',
            message: error instanceof Error ? error.message : String(error),
            source: 'network',
        };
    }
}
